import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { EVENT_NAME_SCROLL } from '../../../../constants/event'
import { useIntersectionObserver } from '../../../../hooks'
import {
  CHATROOM_LOAD_OLDER_OBSERVER_OPTIONS,
  CHATROOM_SCROLL_NEAR_BOTTOM_THRESHOLD_PX,
} from '../../Chatroom.constants'
import { getChatroomOldestMessageUuid } from '../../Chatroom.utils'

import {
  getChatroomIsOwnLastMessage,
  getChatroomScrollIsNearBottom,
  scrollChatroomToBottom,
  scrollChatroomToElement,
} from './useChatroomMessagesScroll.utils'

export function useChatroomMessagesScroll({
  currentUserId,
  hasMoreOlder,
  isInitialReady,
  isLoadingOlder,
  messages,
  unreadAnchorMessageUuid,
  onLoadOlderPage,
  onReadBottomReached,
}) {
  const scrollRef = useRef(null)
  const unreadDividerRef = useRef(null)
  const prevFirstUuidRef = useRef(null)
  const prevLastUuidRef = useRef(null)

  const initialScrollDoneRef = useRef(false)
  const bottomReachedFiredRef = useRef(false)

  const prevScrollHeightRef = useRef(0)

  const [topSentinelElement, setTopSentinelElement] = useState(null)

  function observerCallback(entries) {
    if (entries[0]?.isIntersecting && hasMoreOlder && !isLoadingOlder) {
      const oldestUuid = getChatroomOldestMessageUuid(messages)

      if (oldestUuid && scrollRef.current) {
        prevScrollHeightRef.current = scrollRef.current.scrollHeight
        onLoadOlderPage?.(oldestUuid)
      }
    }
  }

  useIntersectionObserver({
    observerCallback,
    observerOptions: CHATROOM_LOAD_OLDER_OBSERVER_OPTIONS,
    thresholdElement: topSentinelElement,
  })

  // One-shot initial positioning. When there's an unread anchor, the anchor
  // state and the divider DOM node can commit in separate passes — wait for
  // the ref to attach before marking done.
  useLayoutEffect(() => {
    const element = scrollRef.current

    if (
      element &&
      !initialScrollDoneRef.current &&
      isInitialReady &&
      messages.length > 0
    ) {
      const dividerElement = unreadAnchorMessageUuid
        ? unreadDividerRef.current
        : null
      const shouldScroll = !unreadAnchorMessageUuid || dividerElement

      if (shouldScroll) {
        if (dividerElement) {
          scrollChatroomToElement(element, dividerElement)
        } else {
          scrollChatroomToBottom(element)
        }

        initialScrollDoneRef.current = true
        prevFirstUuidRef.current = messages[0]?.uuid
        prevLastUuidRef.current = messages.at(-1)?.uuid
      }
    }
  }, [isInitialReady, messages, unreadAnchorMessageUuid])

  // Older messages prepended — restore visible position by the amount the
  // content grew at the top.
  useLayoutEffect(() => {
    const element = scrollRef.current

    if (element && initialScrollDoneRef.current && messages.length > 0) {
      const firstUuid = messages[0]?.uuid

      if (firstUuid && firstUuid !== prevFirstUuidRef.current) {
        element.scrollTop += element.scrollHeight - prevScrollHeightRef.current
        prevFirstUuidRef.current = firstUuid
      }
    }
  }, [messages])

  // New message at the bottom — auto-scroll if the user is near the bottom
  // or just sent it themselves.
  useLayoutEffect(() => {
    const element = scrollRef.current

    if (element && initialScrollDoneRef.current && messages.length > 0) {
      const lastUuid = messages.at(-1)?.uuid

      if (lastUuid && lastUuid !== prevLastUuidRef.current) {
        const shouldScroll =
          getChatroomScrollIsNearBottom({
            element,
            threshold: CHATROOM_SCROLL_NEAR_BOTTOM_THRESHOLD_PX,
          }) || getChatroomIsOwnLastMessage({ currentUserId, messages })

        if (shouldScroll) {
          scrollChatroomToBottom(element)
        }

        prevLastUuidRef.current = lastUuid
      }
    }
  }, [currentUserId, messages])

  // Fires once per session when the user first reaches the bottom — marks
  // the chat read on the backend and unlocks dynamic divider movement.
  useEffect(() => {
    const element = scrollRef.current

    if (
      element &&
      isInitialReady &&
      onReadBottomReached &&
      !bottomReachedFiredRef.current
    ) {
      function handleScroll() {
        if (!bottomReachedFiredRef.current) {
          const isNearBottom = getChatroomScrollIsNearBottom({
            element,
            threshold: CHATROOM_SCROLL_NEAR_BOTTOM_THRESHOLD_PX,
          })

          if (isNearBottom) {
            bottomReachedFiredRef.current = true
            onReadBottomReached()
          }
        }
      }

      element.addEventListener(EVENT_NAME_SCROLL, handleScroll)
      handleScroll()

      return () => {
        element.removeEventListener(EVENT_NAME_SCROLL, handleScroll)
      }
    }
  }, [isInitialReady, onReadBottomReached])

  return { scrollRef, setTopSentinelElement, unreadDividerRef }
}
